import { useState, useEffect } from "react";
import api, { API } from "@/lib/api";
import { formatApiError, waLink, inr } from "@/lib/utils";
import { X, FileText, Download, MessageCircle, ArrowLeft, Loader2, Receipt } from "lucide-react";

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function defaultInvoiceNumber() {
  const d = new Date();
  return `INV-${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}-${String(d.getHours()).padStart(2, "0")}${String(d.getMinutes()).padStart(2, "0")}`;
}

function buildWaMessage(form) {
  const pkg = Number(form.package_amount || 0);
  const gstPct = Number(form.gst_percent || 0);
  const gstAmt = +(pkg * gstPct / 100).toFixed(2);
  const subtotal = pkg + gstAmt;
  const advance = Number(form.advance_paid || 0);
  const balance = +(subtotal - advance).toFixed(2);
  const lines = [
    `Hi ${form.client_name || "there"}, here is your invoice from *Kahani by Rakesh Janardan*:`,
    "",
    `📄 Invoice: *${form.invoice_number}*`,
    form.event ? `💍 Event: ${form.event}${form.event_date ? ` on ${form.event_date}` : ""}` : null,
    "",
    `Package: ${inr(pkg)}`,
    gstPct > 0 ? `GST (${gstPct}%): ${inr(gstAmt)}` : null,
    gstPct > 0 ? `Subtotal: ${inr(subtotal)}` : null,
    `Advance paid: ${inr(advance)}`,
    `*Balance Due: ${inr(balance)}*`,
    "",
    "The PDF copy will be shared shortly. Please reach out for any queries.",
    "Thank you for choosing Kahani 🌸",
  ].filter(Boolean);
  return lines.join("\n");
}

export default function InvoiceModal({ open, booking, onClose }) {
  const [step, setStep] = useState(1); // 1: form, 2: preview
  const [form, setForm] = useState(() => ({
    booking_id: booking?.id || "",
    invoice_number: defaultInvoiceNumber(),
    invoice_date: todayIso(),
    client_name: booking?.client_name || "",
    client_contact: booking?.contact || "",
    couple_name: booking?.couple_name || "",
    event: booking?.event || "Wedding",
    event_date: booking?.day1 || "",
    location: booking?.location || "",
    package_amount: "",
    gst_percent: "",
    advance_paid: "",
    notes: "",
  }));
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  // iter36 — mirror PP totals + booking event_days into the invoice payload.
  const [ppSynced, setPpSynced] = useState(false);

  // Prefill package_amount/advance_paid from the linked Post-Prod row when
  // the modal opens for a booking that has one.
  useEffect(() => {
    if (!open || !booking?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/invoices/prefill/${booking.id}`);
        if (cancelled || !data?.has_pp) return;
        setForm((f) => ({
          ...f,
          package_amount: String(data.total || 0),
          advance_paid: String(data.paid || 0),
        }));
        setPpSynced(true);
      } catch (err) {
        // Prefill is best-effort — the modal still works without it. Log to
        // the dev console for observability without disturbing the user flow.
        if (process.env.NODE_ENV !== "production") {
          console.debug("Invoice PP prefill skipped:", err);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open, booking?.id]);

  if (!open) return null;

  const pkg = Number(form.package_amount || 0);
  const gstPct = Number(form.gst_percent || 0);
  const gstAmt = +(pkg * gstPct / 100).toFixed(2);
  const subtotal = pkg + gstAmt;
  const advance = Number(form.advance_paid || 0);
  const balance = +(subtotal - advance).toFixed(2);

  const cleanup = () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setStep(1);
    setErr("");
  };

  const close = () => {
    cleanup();
    onClose();
  };

  const generate = async (e) => {
    e?.preventDefault?.();
    setErr("");
    setLoading(true);
    try {
      // iter36 — build per-day breakdown from the parent booking so the PDF
      // can render each date with its own event/side/crew count block.
      // iter39 — also include the unbounded extra_days entries + honor per-day
      // split toggles by expanding into two rows (Bride + Groom) with their
      // respective crew buckets.
      const eventDays = [];
      const pushSlot = (dt, event, side, split, crew, brideCrew, groomCrew) => {
        if (!dt) return;
        if (split) {
          eventDays.push({ date: dt, event, side: "Bride Side", crew: brideCrew || {} });
          eventDays.push({ date: dt, event, side: "Groom Side", crew: groomCrew || {} });
        } else {
          eventDays.push({ date: dt, event, side: side || "Both Side", crew: crew || {} });
        }
      };
      for (const k of ["day1", "day2", "day3", "day4", "day5"]) {
        pushSlot(
          booking?.[k],
          booking?.day_event_types?.[k] || booking?.event || "",
          booking?.day_sides?.[k] || "Both Side",
          !!booking?.day_splits?.[k],
          booking?.crew_requirements?.[k],
          booking?.day_bride_crew?.[k],
          booking?.day_groom_crew?.[k],
        );
      }
      for (const ed of (booking?.extra_days || [])) {
        pushSlot(
          ed?.date,
          ed?.event_type || booking?.event || "",
          ed?.side || "Both Side",
          !!ed?.split,
          ed?.crew,
          ed?.bride_crew,
          ed?.groom_crew,
        );
      }
      const payload = {
        ...form,
        package_amount: Number(form.package_amount || 0),
        gst_percent: Number(form.gst_percent || 0),
        advance_paid: Number(form.advance_paid || 0),
        event_days: eventDays,
        deliverables: Array.isArray(booking?.deliverables) ? booking.deliverables : [],
      };
      const res = await api.post("/invoices/render", payload, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
      setPdfUrl(url);
      setStep(2);
    } catch (e2) {
      let detail = e2.response?.data;
      // If detail is a Blob (because responseType=blob), parse it
      if (detail instanceof Blob) {
        try {
          const txt = await detail.text();
          detail = JSON.parse(txt).detail;
        } catch {
          detail = "Failed to generate invoice";
        }
      } else {
        detail = e2.response?.data?.detail;
      }
      setErr(formatApiError(detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = () => {
    if (!pdfUrl) return;
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = `Invoice_${form.invoice_number}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const waHref = form.client_contact
    ? waLink(form.client_contact, buildWaMessage(form))
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/40 fade-in" onClick={close}>
      <div className="bg-white w-full max-w-3xl h-full flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="invoice-modal">
        {/* Header */}
        <div className="flex items-center justify-between px-6 h-16 border-b border-stone-200 shrink-0">
          <div className="flex items-center gap-3">
            <Receipt className="w-5 h-5 text-rose-700" strokeWidth={1.6} />
            <div>
              <h2 className="font-heading text-lg font-semibold leading-tight">
                {step === 1 ? "Generate invoice" : "Invoice ready"}
              </h2>
              <div className="text-[11px] uppercase tracking-wider text-stone-500">
                {step === 1 ? "Step 1 — Enter details" : "Step 2 — Preview, download or share"}
              </div>
            </div>
          </div>
          <button className="btn-ghost p-2 rounded" onClick={close} data-testid="invoice-close">
            <X className="w-5 h-5" strokeWidth={1.6} />
          </button>
        </div>

        {/* Body */}
        {step === 1 && (
          <form onSubmit={generate} className="flex-1 overflow-y-auto p-6 space-y-4">
            {/* iter137 — Per-Party Split Invoices */}
            {booking?.payment_split?.enabled && (booking?.payment_split?.parties || []).length > 0 && (
              <div
                className="border border-amber-300 bg-amber-50/60 rounded-lg p-4"
                data-testid="invoice-split-section"
              >
                <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                  <div className="text-sm font-semibold text-amber-900">
                    Split Payment enabled — generate per-party invoices
                  </div>
                  <div className="text-[11px] text-amber-800 font-mono">
                    Grand: ₹{Number(booking?.quote?.grand_total || 0).toLocaleString("en-IN")}
                  </div>
                </div>
                <p className="text-[11px] text-stone-600 mb-3">
                  Each party receives their own PDF (unique invoice number suffix, own amount, own contact).
                  The combined invoice below still generates a single PDF for the full contract.
                </p>
                <div className="space-y-2">
                  {(booking.payment_split.parties || []).map((party, idx) => (
                    <SplitPartyInvoiceRow
                      key={party.id || idx}
                      party={party}
                      idx={idx}
                      booking={booking}
                      baseInvoiceNumber={form.invoice_number}
                      invoiceDate={form.invoice_date}
                    />
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Invoice number *</label>
                <input required className="input font-mono" value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} data-testid="invoice-number-input" />
              </div>
              <div>
                <label className="label">Invoice date</label>
                <input type="date" className="input" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} data-testid="invoice-date-input" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Client name *</label>
                <input required className="input" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="invoice-client-input" />
              </div>
              <div>
                <label className="label">Client contact (for WhatsApp)</label>
                <input className="input" value={form.client_contact} onChange={(e) => setForm({ ...form, client_contact: e.target.value })} data-testid="invoice-contact-input" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Couple</label>
                <input className="input" value={form.couple_name} onChange={(e) => setForm({ ...form, couple_name: e.target.value })} data-testid="invoice-couple-input" />
              </div>
              <div>
                <label className="label">Event</label>
                <input className="input" value={form.event} onChange={(e) => setForm({ ...form, event: e.target.value })} data-testid="invoice-event-input" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Event date</label>
                <input type="date" className="input" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} data-testid="invoice-event-date-input" />
              </div>
              <div>
                <label className="label">Location</label>
                <input className="input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="invoice-location-input" />
              </div>
            </div>

            <div className="bg-rose-50/40 border border-rose-100 rounded-lg p-4 space-y-3">
              {ppSynced && (
                <div className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1" data-testid="invoice-pp-synced-hint">
                  ✅ Package &amp; Advance synced from Post-Production ledger. Editable if you need to override.
                </div>
              )}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="label">Package amount (₹) *</label>
                  <input type="number" min="0" step="0.01" required className="input" value={form.package_amount} onChange={(e) => setForm({ ...form, package_amount: e.target.value })} data-testid="invoice-package-input" />
                </div>
                <div>
                  <label className="label">GST % (optional)</label>
                  <input type="number" min="0" max="50" step="0.01" className="input" value={form.gst_percent} onChange={(e) => setForm({ ...form, gst_percent: e.target.value })} placeholder="e.g. 18" data-testid="invoice-gst-input" />
                </div>
                <div>
                  <label className="label">Advance paid (₹)</label>
                  <input type="number" min="0" step="0.01" className="input" value={form.advance_paid} onChange={(e) => setForm({ ...form, advance_paid: e.target.value })} data-testid="invoice-advance-input" />
                </div>
              </div>
              <div className="border-t border-rose-100 pt-3 grid grid-cols-3 gap-3 text-sm">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Subtotal</div>
                  <div className="font-mono tabular-nums text-stone-800">{inr(subtotal)}</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Advance</div>
                  <div className="font-mono tabular-nums text-green-700">− {inr(advance)}</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Balance due</div>
                  <div className="font-mono tabular-nums font-semibold text-rose-700" data-testid="invoice-balance-preview">{inr(balance)}</div>
                </div>
              </div>
            </div>

            <div>
              <label className="label">Notes (optional)</label>
              <textarea className="input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Inclusions, deliverables, payment terms…" data-testid="invoice-notes-input" />
            </div>

            {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn btn-outline" onClick={close}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={loading} data-testid="invoice-generate-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" strokeWidth={1.6} />}
                Generate invoice
              </button>
            </div>
          </form>
        )}

        {step === 2 && pdfUrl && (
          <>
            <div className="flex-1 bg-stone-100 overflow-hidden">
              <iframe
                title="Invoice preview"
                src={pdfUrl}
                className="w-full h-full bg-white"
                data-testid="invoice-pdf-preview"
              />
            </div>
            <div className="border-t border-stone-200 px-6 py-4 flex flex-wrap items-center gap-2 bg-white shrink-0">
              <button className="btn btn-outline" onClick={() => setStep(1)} data-testid="invoice-back-btn">
                <ArrowLeft className="w-4 h-4" strokeWidth={1.6} /> Edit
              </button>
              <div className="flex-1" />
              <button className="btn btn-outline" onClick={downloadPdf} data-testid="invoice-download-btn">
                <Download className="w-4 h-4" strokeWidth={1.6} /> Download PDF
              </button>
              {waHref ? (
                <a href={waHref} target="_blank" rel="noreferrer" className="btn bg-green-600 text-white hover:bg-green-700" data-testid="invoice-whatsapp-btn">
                  <MessageCircle className="w-4 h-4" strokeWidth={1.8} /> Share via WhatsApp
                </a>
              ) : (
                <span className="text-xs text-stone-400 italic">Add a contact to enable WhatsApp share</span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// iter137 — Per-party invoice row inside the Split Payment section
// of the InvoiceModal. Downloads a scoped PDF (party amount + contact).
function SplitPartyInvoiceRow({ party, idx, booking, baseInvoiceNumber, invoiceDate }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const download = async () => {
    setBusy(true); setErr("");
    try {
      const res = await api.post(
        `/bookings/${booking.id}/invoices/split-render`,
        {
          party_id: party.id,
          invoice_number: baseInvoiceNumber,
          invoice_date: invoiceDate,
        },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Invoice_${baseInvoiceNumber}-P${idx + 1}_${(party.name || "party").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="bg-white rounded border border-amber-200 px-3 py-2 flex items-center gap-3 flex-wrap"
      data-testid={`invoice-split-party-${idx}`}
    >
      <div className="flex-1 min-w-[160px]">
        <div className="text-sm font-semibold text-stone-900">{party.name}</div>
        {party.relationship && (
          <div className="text-[11px] text-stone-500">{party.relationship}</div>
        )}
      </div>
      <div className="text-right shrink-0">
        <div className="font-mono text-sm text-stone-900">₹{Number(party.amount || 0).toLocaleString("en-IN")}</div>
        <div className="text-[10px] text-stone-500">#{baseInvoiceNumber}-P{idx + 1}</div>
      </div>
      <button
        type="button"
        className="btn btn-outline btn-sm"
        onClick={download}
        disabled={busy}
        data-testid={`invoice-split-download-${idx}`}
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
        PDF
      </button>
      {err && (
        <div className="w-full text-xs text-red-700">{err}</div>
      )}
    </div>
  );
}
